import React from "react";
import { useTheme } from "styled-components";
import { Box } from "../../Box";
/**
 * Fieldset component provides a styled container for grouping related form elements
 *
 * @component
 * @example
 * <Fieldset legend="Personal Information" required>
 *   <input type="text" name="firstName" />
 *   <input type="text" name="lastName" />
 * </Fieldset>
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Content to be rendered inside the fieldset
 * @param {string} [props.legend] - Optional label text rendered as a legend element
 * @param {boolean} [props.hasError=false] - Whether the fieldset is in an error state
 * @param {boolean} [props.isSuccess=false] - Whether the fieldset is in a success state
 * @param {boolean} [props.disabled=false] - Whether the fieldset is disabled
 * @param {boolean} [props.readOnly=false] - Whether the fieldset is read-only
 * @param {boolean} [props.required=false] - Whether the fieldset is required
 * @param {string} [props.data-testid] - Optional test id for testing
 * @param {React.CSSProperties} [props.sx] - Optional sx prop for custom styling
 *
 * @returns {JSX.Element} Styled fieldset component
 */
export const Fieldset = ({ children, legend, hasError = false, isSuccess = false, disabled = false, readOnly = false, required = false, "data-testid": testId, sx, }) => {
    // Access theme colors from context
    const { colors } = useTheme();
    // Determine border color based on fieldset state
    const getBorderColor = () => {
        if (hasError)
            return colors.inputColors.inputErrorBorder;
        if (isSuccess)
            return colors.inputColors.inputSuccessBorder;
        if (disabled || readOnly)
            return colors.inputColors.inputDisabledBorder;
        return colors.cardColors.cardBorder;
    };
    // Determine background color based on disabled/readonly state
    const getBackgroundColor = () => {
        if (disabled || readOnly)
            return colors.inputColors.fieldsetDisabledBackground;
        return colors.cardColors.cardBackground;
    };
    return (
    // Render fieldset using Box component
    React.createElement(Box, { as: "fieldset", "data-testid": testId, sx: Object.assign({ 
            // Disable pointer events when fieldset is disabled
            "&[disabled]": {
                pointerEvents: "none",
            }, backgroundColor: getBackgroundColor(), border: "1px solid", borderColor: getBorderColor(), borderRadius: 1, cursor: disabled ? "not-allowed" : "default", position: "relative", px: 3, py: 2 }, sx), disabled: disabled, "aria-disabled": disabled, "aria-readonly": readOnly },
        legend && (React.createElement(Box, { as: "legend", sx: {
                color: "inherit",
                fontWeight: 500,
                px: 1,
            } },
            legend,
            required && (React.createElement(Box, { as: "span", style: {
                    color: disabled || readOnly
                        ? colors.inputColors.inputDisabledText
                        : colors.formFieldWrapperColors.requiredStar,
                    marginLeft: "2px",
                } }, "*")))),
        children));
};
